import React, { useContext, useEffect, useRef, useState } from "react";
import Slider from "src/components/slider";
import Actions from "src/components/actionbar";
import { animated, useSpring, useSpringValue } from "@react-spring/web";
import useMeasure from "react-use-measure";
import UserGroups from "./components/userGroup";
import Skills from "./components/skills/skills";
import TimeLine from "./components/timeLine/timeLine";

import _ from "lodash";
import cx from "classnames";
import styles from "./filter.module.scss";
import moment from "moment";
import { CourseReportContext } from "../../context";
import { UserGroup } from "src/types/users";
import { Skill } from "src/types/coaching/courses";
export interface ReportState {
  user: null;
  userGroups: UserGroup[];
  skills: Skill[];
  startDate: Date;
  endDate: Date;
}

interface FilterProps {
  show: boolean;
  onClose: () => void;
  onConfirm: (state: ReportState) => void;
  showUserGroups?: boolean;
}

export const Filters: React.FC<FilterProps> = ({
  show,
  onClose,
  onConfirm,
  showUserGroups = true
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  
  const filterTabs = ["Timeline", "Skills", "User group"].filter(
    (tab) => tab !== "User group" || showUserGroups
  );
  
  const reportContext = useContext(CourseReportContext);

  const [reportState, setReportState] = useState<ReportState>({
    user: reportContext.user,
    userGroups: reportContext.userGroups || [],
    skills: reportContext.skills || [],
    startDate: moment().subtract(30, 'days').startOf('day').toDate(), 
    endDate: moment().endOf('day').toDate(),
  });


  const [saveFilterDialog, setSaveFilterDialog] = useState(false);

  const onReportStateChange = (state) => {
    setReportState(state);
  };

  const clearAll = () => {
    setReportState({
      user: null,
      userGroups: [],
      skills: [],
      startDate: moment().subtract(30, 'days').startOf('day').toDate(),
      endDate: moment().endOf('day').toDate(),
    });
  };

  useEffect(() => {
    setReportState({
      user: reportContext.user,
      userGroups: reportContext.userGroups || [],
      skills: reportContext.skills || [],
      startDate: reportContext.startDate || moment().subtract(30, 'days').startOf('day').toDate(),
      endDate: reportContext.endDate || moment().endOf('day').toDate(),
    });
  }, [reportContext]);

  useEffect(() => {
    if (show) {
        setSelectedTab(0);
    }
}, [show]);

  return (
    <Slider.Dialog
      className={styles.filterSlider}
      width={"32.65rem"}
      show={show}
      onClose={onClose}
    >
      <div className={styles.headerContainer}>
        <div className={styles.header}>
          <Slider.Header title="filters" className={styles.headerText}>
            <p>using timeline, skills & usergroup</p>
          </Slider.Header>
          <p onClick={() => clearAll()} className={styles.clearAllButton}>
            Clear all
          </p>
        </div>
      </div>

      <Slider.Body className={styles.body}>
        <TabBar
          active={selectedTab}
          onSelectionChange={(tab) => setSelectedTab(tab)}
          gap={8}
          className={styles.left}
        >
          {filterTabs.map((tab, index) => (
            <span
              key={tab}
              className={cx(styles.tabs, {
                [styles.active]: selectedTab === index,
              })}
              data-index={index}
            >
              {tab}
            </span>
          ))}
        </TabBar>
        <div className={styles.right}>      
          <TabSection index={0} selected={selectedTab}>
            <TimeLine
              reportState={reportState}
              onReportStateChange={onReportStateChange}
            />
          </TabSection>
          <TabSection index={1} selected={selectedTab}>
            <Skills
              reportState={reportState}
              onReportStateChange={onReportStateChange}
            />
          </TabSection>
          <TabSection index={2} selected={selectedTab}>
            {showUserGroups && (
              <UserGroups
                reportState={reportState}
                onReportStateChange={onReportStateChange}
              />
            )}
          </TabSection>
        </div>
      </Slider.Body>

      <Slider.Actions>
        <div className={styles.footer}>
          <Actions.Button
            className={styles.confirm}
            onClick={() => onConfirm(reportState)}
            title="Confirm"
            icon="paperflite-tick"
          />
          <Actions.Button
            className={styles.cancel}
            onClick={onClose}
            title="Cancel"
          />
        </div>
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface TabBarProps {
  children: React.ReactNode;
  active: number;
  onSelectionChange: (index: number) => void;
  gap?: number;
  className?: string;
}

const TabBar: React.FC<TabBarProps> = ({
  children,
  active,
  onSelectionChange,
  gap = 8,
  className,
}) => {
  const indicatorAnimations = useSpringValue(0);
  const [tabItemRef, bounds] = useMeasure();

  return (
    <div
      className={cx(
        styles.inner,
        {
          [styles.singleTabLayout]: React.Children.count(children) === 1,
        },
        className,
      )}
      style={{ gap: `${gap}px` }}
    >
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return child;
        return (
          <div
            ref={tabItemRef}
            className={cx(styles.tab)}
            onClick={() => onSelectionChange(index)}
          >
            {React.isValidElement(child)
              ? React.cloneElement(child as React.ReactElement<any>, {
                  className: cx((child.props as any).className, {
                    [styles.active]: index === active,
                  }),
                })
              : child}
          </div>
        );
      })}

      <animated.div
        className={styles.indicator}
        style={{ left: indicatorAnimations }}
      />
    </div>
  );
};

interface TabSectionProps {
  selected: number;
  index: number;
  children: React.ReactNode;
}

const TabSection: React.FC<TabSectionProps> = ({
  index,
  selected,
  children,
}) => {
  const active = index === selected;
  const [animation] = useSpring(
    {
      height: active ? "auto" : 0,
      opacity: active ? 1 : 0,
      transform: active
        ? `translate3d(0,0,0)`
        : `translate3d(0, 100%, 0)`,
    },
    [active],
  );

  return (
    <animated.div style={animation}>
      {children}
    </animated.div>
  );
};
